import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import { getData } from "../../../FetchApiServices";
import { T2TKycBadge } from "./T2TVendorActions";
import BulkUploadModal from "./BulkUploadModal";

const STATUS_COLORS = {
  active:       { bg: "#F0FDF4", color: "#16A34A", border: "#BBF7D0" },
  blocked:      { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" },
  deactivated:  { bg: "#F8FAFC", color: "#64748B", border: "#E2E8F0" },
  pending_rekyc:{ bg: "#FFF7ED", color: "#D97706", border: "#FED7AA" },
};

const VERIF_ICONS = { all: "\uD83D\uDFE2", partial: "\uD83D\uDFE1", issues: "\uD83D\uDD34", pending: "\u26AA" };

const T2TVendorList = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "", vendor_type: "", account_group: "", verification: "", kyc_due: false, sap_sync: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  useEffect(() => {
    fetchVendors();
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVendors = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search.trim()) params.append("q", search.trim());
    if (filters.status) params.append("status", filters.status);
    if (filters.vendor_type) params.append("vendor_type", filters.vendor_type);
    if (filters.account_group) params.append("account_group", filters.account_group);
    if (filters.kyc_due) params.append("kyc_due_days", "90");
    if (filters.sap_sync) params.append("sap_sync", filters.sap_sync);

    const resp = await getData(`t2t/vendors?${params.toString()}`);
    if (!resp.error) {
      const raw = resp.data?.data;
      const list = Array.isArray(raw) ? raw : (raw?.vendors || raw?.rows || []);
      setVendors(list);
      setTotal(raw?.total || raw?.count || list.length);
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchVendors();
  };

  const getVerifStatus = (v) => {
    if (v.vendor_type === "domestic") {
      if (v.pan_verified && v.gstin_verified && v.bank_verified) return "all";
      if (v.pan_verified || v.gstin_verified || v.bank_verified) return "partial";
      return "pending";
    }
    if (v.swift_verified && (v.vat_verified || v.tax_verified)) return "all";
    if (v.swift_verified || v.vat_verified || v.tax_verified) return "partial";
    return "pending";
  };

  const maskPan = (pan) => pan ? `${pan.slice(0, 5)}****${pan.slice(9)}` : "\u2014";
  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <T2TTopNav title="Vendor Master" />
      <div className="t2t-content">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <div>
            <div className="t2t-page-title" style={{ marginBottom: 2 }}>Vendor Master</div>
            <p className="t2t-page-sub" style={{ marginBottom: 0 }}>Manage all domestic and international vendors.</p>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0, marginTop: 4 }}>
            <button className="t2t-btn t2t-btn-outline t2t-btn-sm" onClick={() => setBulkOpen(true)}>
              <i className="bi bi-upload me-1" /> Bulk Upload
            </button>
            <button
              className="t2t-btn t2t-btn-primary t2t-btn-sm"
              onClick={() => navigate("/t2t-requests?new=vendor_creation")}
            >
              + New Vendor Request
            </button>
          </div>
        </div>

        {/* Search + Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16, marginTop: 16, flexWrap: "wrap", alignItems: "center" }}>
          <form onSubmit={handleSearch} style={{ display: "flex", gap: 6, flex: 1, minWidth: 200 }}>
            <input className="t2t-fc" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, PAN, GSTIN, vendor number..." style={{ flex: 1 }} />
            <button className="t2t-btn t2t-btn-primary t2t-btn-sm" type="submit">Search</button>
          </form>

          <select className="t2t-fc" style={{ width: 130 }} value={filters.status} onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
            <option value="deactivated">Deactivated</option>
            <option value="pending_rekyc">Pending KYC</option>
          </select>

          <select className="t2t-fc" style={{ width: 140 }} value={filters.vendor_type} onChange={(e) => { setFilters({ ...filters, vendor_type: e.target.value }); setPage(1); }}>
            <option value="">All Types</option>
            <option value="domestic">Domestic</option>
            <option value="international">International</option>
          </select>

          <select className="t2t-fc" style={{ width: 140 }} value={filters.account_group} onChange={(e) => { setFilters({ ...filters, account_group: e.target.value }); setPage(1); }}>
            <option value="">All Groups</option>
            <option value="Z101">Z101 \u2014 Regular</option>
            <option value="Z102">Z102 \u2014 Simplified</option>
            <option value="Z103">Z103 \u2014 Seasonal</option>
            <option value="Z104">Z104 \u2014 Service</option>
            <option value="Z105">Z105 \u2014 One-Time</option>
            <option value="Z201">Z201 \u2014 Intl Regular</option>
            <option value="Z202">Z202 \u2014 Intl Simplified</option>
          </select>

          <select className="t2t-fc" style={{ width: 120 }} value={filters.sap_sync} onChange={(e) => { setFilters({ ...filters, sap_sync: e.target.value }); setPage(1); }}>
            <option value="">SAP Status</option>
            <option value="synced">Synced</option>
            <option value="mock">Mock</option>
            <option value="failed">Failed</option>
          </select>

          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={filters.kyc_due} onChange={(e) => { setFilters({ ...filters, kyc_due: e.target.checked }); setPage(1); }} />
            KYC Due (90d)
          </label>
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>Vendor #</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>Name</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>Group</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>Status</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>PAN / VAT</th>
                <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700 }}>Verified</th>
                <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700 }}>Score</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>SAP #</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>KYC</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700 }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>Loading vendors...</td></tr>
              )}
              {!loading && vendors.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>No vendors found.</td></tr>
              )}
              {vendors.map((v) => {
                const sc = STATUS_COLORS[v.status] || STATUS_COLORS.active;
                const verifStatus = getVerifStatus(v);
                const isMock = v.sap_vendor_number?.startsWith("MOCK");
                const scoreColor = (v.performance_score || 0) >= 80 ? "#16A34A" : (v.performance_score || 0) >= 50 ? "#D97706" : "#DC2626";
                return (
                  <tr key={v.vendor_id} onClick={() => navigate(`/t2t-vendor/${v.vendor_id}`)}
                    style={{ borderBottom: "1px solid #F1F5F9", cursor: "pointer", transition: "0.1s" }}
                    onMouseOver={(e) => { e.currentTarget.style.background = "#F8FAFC"; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = ""; }}>
                    <td style={{ padding: "10px 10px", fontWeight: 600, color: "var(--t2t-primary)", whiteSpace: "nowrap" }}>
                      {v.vendor_number || v.vendor_id}
                    </td>
                    <td style={{ padding: "10px 10px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.vendor_type === "international" && <span title="International" style={{ marginRight: 4 }}>{"\uD83C\uDF10"}</span>}
                      {v.name}
                    </td>
                    <td style={{ padding: "10px 10px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }}>
                        {v.account_group}
                      </span>
                    </td>
                    <td style={{ padding: "10px 10px" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                        {v.status?.replace("_", " ").toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: "10px 10px", fontFamily: "monospace", fontSize: 11 }}>
                      {v.vendor_type === "domestic" ? maskPan(v.pan_number) : (v.vat_number || v.tax_id || "\u2014")}
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "center", fontSize: 14 }}>
                      <span title={verifStatus}>{VERIF_ICONS[verifStatus]}</span>
                    </td>
                    <td style={{ padding: "10px 10px", textAlign: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor }}>{v.performance_score || 0}</span>
                    </td>
                    <td style={{ padding: "10px 10px", fontFamily: "monospace", fontSize: 11 }}>
                      {v.sap_vendor_number ? (
                        <span style={{ color: isMock ? "#D97706" : "#16A34A" }}>
                          {isMock ? "MOCK" : v.sap_vendor_number}
                        </span>
                      ) : "\u2014"}
                    </td>
                    <td style={{ padding: "10px 10px" }}><T2TKycBadge vendor={v} /></td>
                    <td style={{ padding: "10px 10px", color: "#94A3B8", whiteSpace: "nowrap" }}>
                      {v.created_at ? new Date(v.created_at).toLocaleDateString() : "\u2014"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>{"\u2190"} Prev</button>
            <span style={{ padding: "4px 12px", fontSize: 12, color: "var(--t2t-text-muted)" }}>Page {page} of {totalPages}</span>
            <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next {"\u2192"}</button>
          </div>
        )}
      </div>

      {bulkOpen && (
        <BulkUploadModal
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); fetchVendors(); }}
        />
      )}
    </>
  );
};

export default T2TVendorList;
